import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UsersIcon, MailIcon, PhoneIcon, BuildingIcon, FilterIcon, BriefcaseIcon, MapPinIcon, TrendingUpIcon, XIcon, ClockIcon, TagIcon, MessageSquareIcon, CalendarIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
type FilterType = 'all' | 'client' | 'quad';
export function People() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  // Aggregate all people from all accounts
  const allPeople = useMemo(() => {
    const people: any[] = [];
    seedData.accounts.forEach(account => {
      // Add client contacts
      if (account.client_contacts) {
        account.client_contacts.forEach(person => {
          people.push({
            ...person,
            type: 'client',
            accountName: account.name,
            accountId: account.id,
            accountLogo: account.logo_url,
            accountHealth: account.health
          });
        });
      }
      // Add Quad team
      if (account.quad_team) {
        account.quad_team.forEach(person => {
          people.push({
            ...person,
            type: 'quad',
            accountName: account.name,
            accountId: account.id,
            accountLogo: account.logo_url,
            accountHealth: account.health
          });
        });
      }
    });
    return people;
  }, []);
  // Filter and search
  const filteredPeople = useMemo(() => {
    return allPeople.filter(person => {
      const matchesSearch = searchQuery === '' || person.name.toLowerCase().includes(searchQuery.toLowerCase()) || person.email.toLowerCase().includes(searchQuery.toLowerCase()) || person.role_title.toLowerCase().includes(searchQuery.toLowerCase()) || person.accountName.toLowerCase().includes(searchQuery.toLowerCase()) || person.function?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === 'all' || filterType === 'client' && person.side === 'Client' || filterType === 'quad' && person.side === 'Quad';
      return matchesSearch && matchesFilter;
    });
  }, [allPeople, searchQuery, filterType]);
  // Calculate statistics
  const stats = useMemo(() => {
    const clientCount = allPeople.filter(p => p.side === 'Client').length;
    const quadCount = allPeople.filter(p => p.side === 'Quad').length;
    // Count by function
    const functionCounts = allPeople.reduce((acc, person) => {
      const func = person.function || 'Other';
      acc[func] = (acc[func] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    // Get top 3 functions
    const topFunctions = Object.entries(functionCounts).sort(([, a], [, b]) => b - a).slice(0, 3);
    // Count unique accounts with people
    const uniqueAccounts = new Set(allPeople.map(p => p.accountId)).size;
    return {
      total: allPeople.length,
      clientCount,
      quadCount,
      topFunctions,
      uniqueAccounts
    };
  }, [allPeople]);
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };
  return <div className="space-y-8" data-id="element-2198">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-2199">
        <div className="absolute inset-0 -z-10" data-id="element-2200">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-2201" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2202" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-slate-950/95 border border-slate-800/80 rounded-3xl p-10 shadow-2xl shadow-black/40 backdrop-blur-xl" data-id="element-2203">
          <div className="flex items-start justify-between" data-id="element-2204">
            <div data-id="element-2205">
              <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white via-purple-50 to-slate-200 bg-clip-text text-transparent" data-id="element-2206">
                People Directory
              </h1>
              <p className="text-slate-400 text-lg flex items-center gap-2" data-id="element-2207">
                <UsersIcon className="w-5 h-5 text-purple-400" data-id="element-2208" />
                {filteredPeople.length} contacts across {stats.uniqueAccounts}{' '}
                accounts
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      {/* Enhanced Statistics Cards */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2209">
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2210">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2211" />
          <div className="relative" data-id="element-2212">
            <div className="flex items-center justify-between mb-4" data-id="element-2213">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-2214">
                <UsersIcon className="w-6 h-6 text-purple-400" data-id="element-2215" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2216">
                {stats.total}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2217">
              Total Contacts
            </p>
          </div>
        </div>
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2218">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2219" />
          <div className="relative" data-id="element-2220">
            <div className="flex items-center justify-between mb-4" data-id="element-2221">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors" data-id="element-2222">
                <BuildingIcon className="w-6 h-6 text-blue-400" data-id="element-2223" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2224">
                {stats.clientCount}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2225">
              Client Contacts
            </p>
          </div>
        </div>
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2226">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2227" />
          <div className="relative" data-id="element-2228">
            <div className="flex items-center justify-between mb-4" data-id="element-2229">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-2230">
                <BriefcaseIcon className="w-6 h-6 text-purple-400" data-id="element-2231" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2232">
                {stats.quadCount}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2233">
              Quad Team
            </p>
          </div>
        </div>
        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2234">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2235" />
          <div className="relative" data-id="element-2236">
            <div className="flex items-center justify-between mb-4" data-id="element-2237">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors" data-id="element-2238">
                <BuildingIcon className="w-6 h-6 text-amber-400" data-id="element-2239" />
              </div>
              <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2240">
                {stats.uniqueAccounts}
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-400 group-hover:text-slate-300 transition-colors" data-id="element-2241">
              Active Accounts
            </p>
          </div>
        </div>
      </motion.div>
      {/* Enhanced Search & Filter Bar */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30" data-id="element-2242">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-2243">
          {/* Search Input */}
          <div className="flex-1 relative" data-id="element-2244">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2245" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, email, role, function, or account..." className="w-full pl-12 pr-4 py-4 bg-slate-900/60 border border-slate-800/80 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500/40 transition-all duration-200 shadow-lg shadow-black/20" data-id="element-2246" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-white transition-colors" data-id="element-2247">
                <XIcon className="w-4 h-4" data-id="element-2248" />
              </button>}
          </div>
          {/* Filter Buttons */}
          <div className="flex gap-2 pb-1" data-id="element-2249">
            <button onClick={() => setFilterType('all')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${filterType === 'all' ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white ring-1 ring-purple-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2250">
              All ({stats.total})
            </button>
            <button onClick={() => setFilterType('client')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${filterType === 'client' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white ring-1 ring-blue-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2251">
              Client ({stats.clientCount})
            </button>
            <button onClick={() => setFilterType('quad')} className={`px-6 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 ${filterType === 'quad' ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white ring-1 ring-purple-500/30 scale-[1.01]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'}`} data-id="element-2252">
              Quad Team ({stats.quadCount})
            </button>
          </div>
        </div>
      </motion.div>
      {/* Enhanced People Grid */}
      {filteredPeople.length === 0 ? <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-16 shadow-2xl shadow-black/30 text-center" data-id="element-2253">
          <div className="w-20 h-20 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-6" data-id="element-2254">
            <UsersIcon className="w-10 h-10 text-slate-500" data-id="element-2255" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-2256">No People Found</h3>
          <p className="text-slate-400" data-id="element-2257">
            Try adjusting your search or filter criteria
          </p>
        </motion.div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2258">
          {filteredPeople.map((person, index) => <motion.div key={`${person.id}-${person.accountId}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1 + index * 0.02
      }} onClick={() => setSelectedPerson(person)} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-purple-500/40 transition-all duration-300 cursor-pointer overflow-hidden hover:scale-[1.02]" data-id="element-2259">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2260" />
              <div className="relative" data-id="element-2261">
                {/* Header with Avatar and Type Badge */}
                <div className="flex items-start justify-between mb-4" data-id="element-2262">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-xl font-bold shadow-xl ring-2 ring-purple-500/20 group-hover:ring-purple-500/40 group-hover:scale-110 transition-all duration-300" data-id="element-2263">
                    {person.name.charAt(0)}
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${person.side === 'Client' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-purple-500/10 border-purple-500/30 text-purple-400'}`} data-id="element-2264">
                    {person.side === 'Client' ? 'Client' : 'Quad Team'}
                  </div>
                </div>
                {/* Name and Role */}
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-purple-400 transition-colors" data-id="element-2265">
                  {person.name}
                </h3>
                <p className="text-sm text-slate-400 mb-1 flex items-center gap-2" data-id="element-2266">
                  <BriefcaseIcon className="w-3.5 h-3.5" data-id="element-2267" />
                  {person.role_title}
                </p>
                {person.function && <p className="text-xs text-slate-500 mb-4 flex items-center gap-2" data-id="element-2268">
                    <TagIcon className="w-3 h-3" data-id="element-2269" />
                    {person.function}
                  </p>}
                {/* Contact Info */}
                <div className="space-y-2.5 mb-4" data-id="element-2270">
                  <div className="flex items-center gap-2.5 text-xs text-slate-400 group/item hover:text-white transition-colors" data-id="element-2271">
                    <MailIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2272" />
                    <span className="truncate" data-id="element-2273">{person.email}</span>
                  </div>
                  {person.phone && <div className="flex items-center gap-2.5 text-xs text-slate-400 group/item hover:text-white transition-colors" data-id="element-2274">
                      <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2275" />
                      <span data-id="element-2276">{person.phone}</span>
                    </div>}
                  {person.location && <div className="flex items-center gap-2.5 text-xs text-slate-400 group/item hover:text-white transition-colors" data-id="element-2277">
                      <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2278" />
                      <span data-id="element-2279">{person.location}</span>
                    </div>}
                  {person.last_touch && <div className="flex items-center gap-2.5 text-xs text-slate-500" data-id="element-2280">
                      <ClockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-2281" />
                      <span data-id="element-2282">
                        Last contact: {getRelativeTime(person.last_touch)}
                      </span>
                    </div>}
                </div>
                {/* Tags */}
                {person.tags && person.tags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-2283">
                    {person.tags.slice(0, 2).map((tag: string, i: number) => <span key={i} className="px-2 py-1 bg-slate-800/60 text-slate-400 text-xs rounded-md border border-slate-700/50" data-id="element-2284">
                        {tag}
                      </span>)}
                  </div>}
                {/* Account Badge */}
                <div className="pt-4 border-t border-slate-800/60 group-hover:border-slate-700/60 transition-colors" data-id="element-2285">
                  <div className="flex items-center gap-3" data-id="element-2286">
                    <div className="w-8 h-8 rounded-lg bg-white/95 p-1.5 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-2287">
                      <img src={person.accountLogo} alt={person.accountName} className="w-full h-full object-contain" data-id="element-2288" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2289">
                      <span className="text-xs font-semibold text-slate-400 truncate group-hover:text-slate-300 transition-colors block" data-id="element-2290">
                        {person.accountName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>}
      {/* Person Detail Modal */}
      <AnimatePresence data-id="element-2291">
        {selectedPerson && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedPerson(null)} className="fixed inset-0 bg-black/75 backdrop-blur-md z-[100]" data-id="element-2292" />
            <div className="fixed inset-0 z-[101] overflow-y-auto" data-id="element-2293">
              <div className="flex min-h-full items-center justify-center p-4" data-id="element-2294">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800/80 rounded-3xl shadow-2xl overflow-hidden" data-id="element-2295">
                  {/* Modal Header */}
                  <div className="relative px-8 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950" data-id="element-2296">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5 pointer-events-none" data-id="element-2297" />
                    <div className="relative flex items-start justify-between" data-id="element-2298">
                      <div className="flex items-center gap-4" data-id="element-2299">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-2xl ring-2 ring-purple-500/30" data-id="element-2300">
                          {selectedPerson.name.charAt(0)}
                        </div>
                        <div data-id="element-2301">
                          <h2 className="text-2xl font-bold text-white mb-1 bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent" data-id="element-2302">
                            {selectedPerson.name}
                          </h2>
                          <p className="text-slate-400 flex items-center gap-2 mb-1" data-id="element-2303">
                            <BriefcaseIcon className="w-4 h-4" data-id="element-2304" />
                            {selectedPerson.role_title}
                          </p>
                          {selectedPerson.function && <p className="text-sm text-slate-500 flex items-center gap-2" data-id="element-2305">
                              <TagIcon className="w-3.5 h-3.5" data-id="element-2306" />
                              {selectedPerson.function}
                            </p>}
                        </div>
                      </div>
                      <button onClick={() => setSelectedPerson(null)} className="p-2.5 hover:bg-slate-800/60 rounded-xl transition-all text-slate-400 hover:text-white hover:scale-105" data-id="element-2307">
                        <XIcon className="w-6 h-6" data-id="element-2308" />
                      </button>
                    </div>
                  </div>
                  {/* Modal Content */}
                  <div className="p-8 space-y-6 max-h-[calc(85vh-140px)] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-2309">
                    {/* Type Badge */}
                    <div className={`inline-flex px-4 py-2 rounded-xl text-sm font-bold border shadow-lg ${selectedPerson.side === 'Client' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-purple-500/10 border-purple-500/30 text-purple-400'}`} data-id="element-2310">
                      {selectedPerson.side === 'Client' ? 'Client Contact' : 'Quad Team Member'}
                    </div>
                    {/* Tags */}
                    {selectedPerson.tags && selectedPerson.tags.length > 0 && <div className="space-y-3" data-id="element-2311">
                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-2312">
                          <TagIcon className="w-4 h-4" data-id="element-2313" />
                          Tags
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2314">
                          {selectedPerson.tags.map((tag: string, i: number) => <span key={i} className="px-3 py-1.5 bg-slate-800/60 text-slate-300 text-sm rounded-lg border border-slate-700/50" data-id="element-2315">
                              {tag}
                            </span>)}
                        </div>
                      </div>}
                    {/* Contact Information */}
                    <div className="space-y-4" data-id="element-2316">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-2317">
                        Contact Information
                      </h3>
                      <div className="space-y-3" data-id="element-2318">
                        <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-2319">
                          <MailIcon className="w-5 h-5 text-blue-400 flex-shrink-0" data-id="element-2320" />
                          <div data-id="element-2321">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-2322">Email</p>
                            <a href={`mailto:${selectedPerson.email}`} className="text-sm text-white hover:text-blue-400 transition-colors" data-id="element-2323">
                              {selectedPerson.email}
                            </a>
                          </div>
                        </div>
                        {selectedPerson.phone && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-2324">
                            <PhoneIcon className="w-5 h-5 text-green-400 flex-shrink-0" data-id="element-2325" />
                            <div data-id="element-2326">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-2327">
                                Phone
                              </p>
                              <a href={`tel:${selectedPerson.phone}`} className="text-sm text-white hover:text-green-400 transition-colors" data-id="element-2328">
                                {selectedPerson.phone}
                              </a>
                            </div>
                          </div>}
                        {selectedPerson.location && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-2329">
                            <MapPinIcon className="w-5 h-5 text-purple-400 flex-shrink-0" data-id="element-2330" />
                            <div data-id="element-2331">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-2332">
                                Location
                              </p>
                              <p className="text-sm text-white" data-id="element-2333">
                                {selectedPerson.location}
                              </p>
                            </div>
                          </div>}
                        {selectedPerson.last_touch && <div className="flex items-center gap-3 p-4 bg-slate-900/60 border border-slate-800/60 rounded-xl" data-id="element-2334">
                            <ClockIcon className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-2335" />
                            <div data-id="element-2336">
                              <p className="text-xs text-slate-500 mb-1" data-id="element-2337">
                                Last Contact
                              </p>
                              <p className="text-sm text-white" data-id="element-2338">
                                {getRelativeTime(selectedPerson.last_touch)}
                              </p>
                            </div>
                          </div>}
                      </div>
                    </div>
                    {/* Account Information */}
                    <div className="space-y-4" data-id="element-2339">
                      <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-2340">
                        Account
                      </h3>
                      <div className="flex items-center gap-4 p-5 bg-slate-900/60 border border-slate-800/60 rounded-xl hover:border-slate-700/60 transition-all" data-id="element-2341">
                        <div className="w-14 h-14 rounded-xl bg-white/95 p-2.5 flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-2342">
                          <img src={selectedPerson.accountLogo} alt={selectedPerson.accountName} className="w-full h-full object-contain" data-id="element-2343" />
                        </div>
                        <div data-id="element-2344">
                          <p className="text-sm font-semibold text-white mb-1" data-id="element-2345">
                            {selectedPerson.accountName}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-2346">
                            Client Account
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}